import {
  CRow,
  CCol,
  CInput,
  CLabel,
  CFormGroup,
  CInputGroup,
  CCard,
  CCardHeader,
  CCardBody,
  CCardTitle,
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
} from '@coreui/react';
import React, { useState } from 'react';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import _t from 'counterpart';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { findErrorFromValidation, handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import QueryDataRenderer from '../../components/QueryDataRenderer';
import CIcon from '@coreui/icons-react';
import apiClient from '../../apiClient';

interface MetaPixel {
  id: number;
  pixelId: string;
  pixelAccessToken: string;
  createdAt: string;
  updatedAt: string;
}

const MetaPixelIntegration = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPixel, setEditingPixel] = useState<MetaPixel | null>(null);
  const [deletingPixel, setDeletingPixel] = useState<MetaPixel | null>(null);
  const [pixelId, setPixelId] = useState('');
  const [pixelAccessToken, setPixelAccessToken] = useState('');

  const pixelsQuery = useQuery<MetaPixel[]>('meta-pixels', async () => {
    const response = await apiClient.get('/api/admin/meta-pixels');
    return response.data;
  });

  const createMutation = useMutation(
    async (data: { pixelId: string; pixelAccessToken: string }) => {
      return await apiClient.post('/api/admin/meta-pixels', data);
    },
    {
      onError: (error: any) => {
        handleFormError(error);
      },
      onSuccess: () => {
        toast.success(_t('integrations.meta-pixel-created'));
        queryClient.invalidateQueries('meta-pixels');
        handleCloseModal();
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }: { id: number; data: { pixelId: string; pixelAccessToken: string } }) => {
      return await apiClient.put(`/api/admin/meta-pixels/${id}`, data);
    },
    {
      onError: (error: any) => {
        handleFormError(error);
      },
      onSuccess: () => {
        toast.success(_t('integrations.meta-pixel-updated'));
        queryClient.invalidateQueries('meta-pixels');
        handleCloseModal();
      },
    }
  );

  const deleteMutation = useMutation(
    async (id: number) => {
      return await apiClient.delete(`/api/admin/meta-pixels/${id}`);
    },
    {
      onError: (error: any) => {
        handleFormError(error);
        setShowDeleteModal(false);
        setDeletingPixel(null);
      },
      onSuccess: () => {
        toast.success(_t('integrations.meta-pixel-deleted'));
        queryClient.invalidateQueries('meta-pixels');
        setShowDeleteModal(false);
        setDeletingPixel(null);
      },
    }
  );

  const handleOpenModal = (pixel?: MetaPixel) => {
    if (pixel) {
      setEditingPixel(pixel);
      setPixelId(pixel.pixelId);
      setPixelAccessToken(pixel.pixelAccessToken);
    } else {
      setEditingPixel(null);
      setPixelId('');
      setPixelAccessToken('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPixel(null);
    setPixelId('');
    setPixelAccessToken('');
  };

  const handleSave = () => {
    const data = {
      pixelId: pixelId.trim(),
      pixelAccessToken: pixelAccessToken.trim(),
    };

    if (editingPixel) {
      updateMutation.mutate({ id: editingPixel.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleOpenDeleteModal = (pixel: MetaPixel) => {
    setDeletingPixel(pixel);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (!deleteMutation.isLoading) {
      setShowDeleteModal(false);
      setDeletingPixel(null);
    }
  };

  const handleConfirmDelete = () => {
    if (deletingPixel && !deleteMutation.isLoading) {
      deleteMutation.mutate(deletingPixel.id);
    }
  };

  const findError = (paramName: string) => {
    const error = editingPixel ? updateMutation.error : createMutation.error;
    return findErrorFromValidation(error, paramName);
  };

  const isFormValid = pixelId.trim() !== '' && pixelAccessToken.trim() !== '';
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <CCol md={12}>
      <CCard>
        <CCardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <CCardTitle>{_t('integrations.meta-pixel-integration')}</CCardTitle>
              <p className="mb-0 mt-1 small">{_t('integrations.meta-pixel-integration-description')}</p>
            </div>
            <CButton className="mr-2 header-button-add" onClick={() => handleOpenModal()}></CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <QueryDataRenderer
            isError={Boolean(pixelsQuery.error)}
            isLoading={pixelsQuery.isLoading}
            onRefetch={() => pixelsQuery.refetch()}
          >
            {pixelsQuery.data && pixelsQuery.data.length === 0 ? (
              <div className="text-center text-muted py-4">
                <p>{_t('integrations.no-meta-pixels-configured')}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>{_t('integrations.pixel-id')}</th>
                      <th>{_t('integrations.pixel-access-token')}</th>
                      <th>{_t('integrations.created-at')}</th>
                      <th className="text-right">{_t('global.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pixelsQuery.data?.map((pixel) => (
                      <tr key={pixel.id}>
                        <td>{pixel.pixelId}</td>
                        <td>
                          <code>{'•'.repeat(20)}</code>
                        </td>
                        <td>{new Date(pixel.createdAt).toLocaleDateString()}</td>
                        <td className="text-right">
                          <CButton
                            color="primary"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleOpenModal(pixel)}
                            disabled={deleteMutation.isLoading}
                          >
                            <CIcon name="cilPencil" />
                          </CButton>
                          <CButton
                            color="danger"
                            size="sm"
                            onClick={() => handleOpenDeleteModal(pixel)}
                            disabled={deleteMutation.isLoading}
                          >
                            <CIcon name="cilTrash" />
                          </CButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </QueryDataRenderer>
        </CCardBody>
      </CCard>

      <CModal show={showModal} onClose={handleCloseModal} size="lg">
        <CModalHeader closeButton>
          <CModalTitle>
            {editingPixel ? _t('integrations.edit-meta-pixel') : _t('integrations.add-meta-pixel')}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CRow>
            <CCol md={12}>
              <CFormGroup>
                <CLabel htmlFor="pixel-id">{_t('integrations.pixel-id')}</CLabel>
                <CInputGroup>
                  <CInput
                    type="text"
                    id="pixel-id"
                    name="pixelId"
                    value={pixelId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPixelId(e.target.value)}
                    placeholder="123456789012345"
                    className="instrument-input-height"
                  />
                </CInputGroup>
                {findError('pixelId') && <CLabel className="text-danger">{findError('pixelId')}</CLabel>}
              </CFormGroup>
            </CCol>
            <CCol md={12}>
              <CFormGroup>
                <CLabel htmlFor="pixel-access-token">{_t('integrations.pixel-access-token')}</CLabel>
                <CInputGroup>
                  <CInput
                    type="text"
                    id="pixel-access-token"
                    name="pixelAccessToken"
                    value={pixelAccessToken}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPixelAccessToken(e.target.value)}
                    placeholder="••••••••••••••••"
                    className="instrument-input-height"
                  />
                </CInputGroup>
                {findError('pixelAccessToken') && (
                  <CLabel className="text-danger">{findError('pixelAccessToken')}</CLabel>
                )}
              </CFormGroup>
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <ButtonWithLoader
            isLoading={isSaving}
            onClick={handleSave}
            buttonColor="primary"
            spinnerColor="secondary"
            title={_t('action.save')}
            className="mr-2"
            showText={false}
            disabled={!isFormValid}
          />
          <CButton color="secondary" onClick={handleCloseModal} disabled={isSaving}>
            {_t('action.cancel')}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal show={showDeleteModal} onClose={handleCloseDeleteModal}>
        <CModalHeader closeButton>
          <CModalTitle>{_t('integrations.delete-meta-pixel')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{_t('integrations.meta-pixel-delete-confirm')}</p>
        </CModalBody>
        <CModalFooter>
          <ButtonWithLoader
            isLoading={deleteMutation.isLoading}
            onClick={handleConfirmDelete}
            buttonColor="danger"
            spinnerColor="secondary"
            title={_t('action.delete')}
            className="mr-2"
            showText={false}
            disabled={deleteMutation.isLoading}
          />
          <CButton color="secondary" onClick={handleCloseDeleteModal} disabled={deleteMutation.isLoading}>
            {_t('action.cancel')}
          </CButton>
        </CModalFooter>
      </CModal>
    </CCol>
  );
};

export default MetaPixelIntegration;
